import React, { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import "../PlateDetails/PlateDetails.scss";
import {
  getPlateCaption,
  getPlateDisplayImageUrl,
  getPlateLocationCountry,
  getPlateLocationState,
  getPlateNumber,
  getProfileUser,
  mapPlateStatusLabel,
} from "../../../utils/platesProfileHelpers";

function PlateManagementView() {
  const location = useLocation();
  const plateData = location.state?.plateData;
  const plateEntry = plateData?.plateEntry;

  const displayData = useMemo(() => {
    if (!plateData) return null;

    const formatDate = (dateString) => {
      if (!dateString) return "N/A";
      try {
        const date = new Date(dateString);
        const dateStr = date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
        const timeStr = date.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
        return `${dateStr} | ${timeStr}`;
      } catch (e) {
        return dateString;
      }
    };

    const user = getProfileUser(plateData);
    const userName =
      plateData.businessName ||
      plateData.profilename ||
      plateData.username ||
      user.name ||
      user.fullName ||
      `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
      user.email ||
      "N/A";
    const userEmail = plateData.businessEmail || user.email || "N/A";
    const userId = user._id || user.id || "N/A";

    const kycStatusMap = { verified: "Verified", unverified: "Unverified", pending: "Pending" };
    const kycStatus =
      kycStatusMap[user.kycVerification?.toLowerCase()] ||
      user.kycVerification?.charAt(0).toUpperCase() + user.kycVerification?.slice(1) ||
      "Unverified";

    const profileType =
      user.profileVisibility?.charAt(0).toUpperCase() +
        user.profileVisibility?.slice(1) || "Public";

    const plateStatus = mapPlateStatusLabel(plateEntry?.status ?? plateData.status);
    const plateImage =
      getPlateDisplayImageUrl(plateEntry, plateData) ||
      process.env.PUBLIC_URL + "/images/plate.svg";

    return {
      sections: [
        {
          title: "Plate Information",
          items: [
            { label: "Plate Number", value: getPlateNumber(plateEntry, plateData) },
            { label: "Claim Status", value: plateStatus, type: "status" },
            {
              label: "Last Updated",
              value: formatDate(plateEntry?.updatedAt || plateData.updatedAt || plateData.createdAt),
            },
            {
              label: "State / Region",
              value: getPlateLocationState(plateEntry, plateData) || "N/A",
            },
            {
              label: "Country",
              value: getPlateLocationCountry(plateEntry, plateData) || "N/A",
            },
            {
              label: "Plate entry ID",
              value: plateEntry?._id || plateData._id || plateData.claimId || "N/A",
            },
          ],
        },
        {
          title: "Media",
          items: [{ label: "Plate Photo", image: plateImage, type: "image" }],
        },
        ...(getPlateCaption(plateEntry) || plateData.caption
          ? [
              {
                title: "Additional Information",
                items: [
                  {
                    label: "Caption",
                    value: getPlateCaption(plateEntry) || plateData.caption,
                  },
                ],
              },
            ]
          : []),
        {
          title: "User Details",
          items: [
            { label: "User Name", value: userName },
            { label: "User ID", value: userId },
            { label: "Email", value: userEmail },
            {
              label: "KYC Status",
              value: kycStatus,
              type: kycStatus === "Verified" ? "status-approved" : "status",
            },
            { label: "Profile Type", value: profileType },
          ],
        },
      ],
    };
  }, [plateData, plateEntry]);

  if (!plateData) {
    return (
      <React.Fragment>
        <header className="plate-detail-panel-header items-center gap-3 flex content-justify-between">
          <div className="left-side">
            <Link to="/plates-management">
              <img src={process.env.PUBLIC_URL + "/images/back.svg"} alt="back" />
            </Link>
            <h4 className="title">Plate Details</h4>
          </div>
        </header>
        <div className="plateDetails">
          <div className="card">
            <p>No plate data available. Please select a plate from the list.</p>
          </div>
        </div>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <header className="plate-detail-panel-header items-center gap-3 flex content-justify-between">
        <div className="left-side">
          <Link to="/plates-management">
            <img src={process.env.PUBLIC_URL + "/images/back.svg"} alt="back" />
          </Link>
          <h4 className="title">Plate Details</h4>
        </div>
      </header>

      <div className="plateDetails">
        <div className="row">
          {displayData.sections.map((section, index) => (
            <div
              key={index}
              className={`col-lg-${section.title === "Media" ? "12" : "12"} col-xl-${
                section.title === "Plate Information" ? "8" : "12"
              } `}
            >
              <div className="card">
                <h4>{section.title}</h4>
                <div className="inner-content">
                  <div className="row">
                    {section.items.map((item, i) => (
                      <div
                        key={i}
                        className={`col-lg-${section.title === "Media" ? "12" : "4"} col-xl-${
                          section.title === "Media" ? "12" : "4"
                        } `}
                      >
                        <div className="content-wrapper">
                          <img src={process.env.PUBLIC_URL + "/images/user-icon.svg"} alt="icon" />
                          <div className="content">
                            <h5>{item.label}</h5>
                            {item.type === "status" && (
                              <p className={`status-badge ${item.value?.toLowerCase() || "pending"}`}>
                                <span className="dot"></span> {item.value}
                              </p>
                            )}
                            {item.type === "status-approved" && (
                              <p className="status-badge approved">
                                <span className="dot"></span> {item.value}
                              </p>
                            )}
                            {item.type === "image" && (
                              <div className="car-plate">
                                <img src={item.image} alt={item.label || "image"} />
                              </div>
                            )}
                            {!item.type && <p>{item.value}</p>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </React.Fragment>
  );
}

export default PlateManagementView;
